import { Button } from "@/components/ui/button";
import { Link, useNavigate, useRouter } from "@tanstack/react-router";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import QuickLinks from "./QuickLinks";
import {Route as InventoryRoute} from '@/routes/(pharma)/_layout/inventory/index'
import { CheckCircle, ChevronRight } from "lucide-react";
import Analytics from "./analytics";
import Health from "./health";
import { PharmacySize, StockPointType } from "@/gen/auth/common_pb";
import { convertToCurrencyRounded } from "@/utils/currency_converter";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { TailSpin } from "react-loader-spinner";
import { useState } from "react";
import { TransferHistoryStatus } from "@/gen/inventory/common_pb";
import { RequestStatus } from "@/gen/purchase_request/common_pb";
import { OrderStatus } from "@/gen/purchase_order/common_pb";

const ActivityComponent = ({
  label,
  description,
  type,
  id
}: {
  label: string;
  description: React.ReactNode;
  type: "purchase_order" | "purchase_invoice" | "purchase_request" | "transfer";
  id: string;
}) => {
   const router = useRouter()
  return (
    <div className="grid grid-cols-5 border-b-[1px] py-2 overflow-hidden cursor-pointer" onClick={() => {
        if (type === "purchase_order") {
            router.navigate({
                to: `/inventory/purchase-order/${id}`,
                params: { id }
            });
        } else if (type === "purchase_invoice") {
            router.navigate({
                to: `/inventory/purchase-invoice/${id}`,
                params: { id }
            });
        } else if (type === "purchase_request") {
            router.navigate({
                to: `/inventory/purchase-request/${id}`,
                params: { id }
            });
        } else if (type === "transfer") {
            
        }
    }}>
      <div className="col-span-4 w-full overflow-hidden">
        <p className="text-sm truncat font-regular">{description}</p>
        <p className="text-xs text-gray-400">{label}</p>
      </div>
      <div className="col-span-1 col-start-6 flex items-center justify-center space-x-2">
        {/* <p className="border rounded-lg px-2 py-1 text-center text-sm font-semibold">
          Documentation
        </p> */}
      </div>
    </div>
  );
};

const OverviewCard = ({
        title,
        value,
        extra,
        color,
        link
    }: {
        title: string;
        value: string | React.ReactNode;
        extra?: string;
        color?: string;
        link?: string;
    }) => {
        return (
            <div className="border-[2px] rounded-lg py-4 px-8 relative">
                <p className="font-medium">{title}</p>
                <p className="font-semibold text-2xl">
                    {value}{" "} 
                    {link ? (
                        <Link to={link}>
                            <Button className="underline hover:no-underline" size="sm" variant="link">
                                Review All
                            </Button>
                        </Link>
                    ) : (
                        <></>
                    )}
                    <span className={`ml-2 text-${color ? color : 'gray'}-500 text-sm font-normal`}>{extra}</span>
                </p>
                {link && <ChevronRight className="h-5 w-5 text-gray-500 absolute right-4 top-4" />}
            </div>
        );
};

const Component = () => {
    const data = InventoryRoute.useLoaderData()
    // const searchParams = InventoryRoute.useSearch()
    // const navigate = useNavigate()
    const [activityView, setActivityView] = useState("all");

    const stockPointType = localStorage.getItem('stockPointType')
    const pharmacySize = localStorage.getItem('pharmacySize')

    return (
        <div className="space-y-6 overflow-y-auto h-screen pb-48">
            <div className="flex items-center justify-between">
                <div className="flex gap-x-4">
                    <h2 className="text-4xl font-bold tracking-tight">
                        Inventory Overview
                    </h2>
                </div>
                <div className="flex flex-row border border-gray-200 rounded-md bg-white">
                    {(stockPointType === StockPointType.RECEIVING.toString() || pharmacySize === PharmacySize.SIZE_SMALL.toString()) && <Link
                        to="/inventory/add-stock"
                    >
                        <Button
                            className="bg-slate-50 text-black-200 border-r-[1px] rounded-l-xs rounded-r-none hover:bg-slate-300"
                        >Add Stock</Button>
                    </Link>}
                    {(stockPointType === StockPointType.RECEIVING.toString() || pharmacySize === PharmacySize.SIZE_SMALL.toString()) && <Link
                        to="/inventory/purchase-invoice/create"
                        preload="intent"
                    >
                        <Button
                            className= "bg-slate-50 text-black-200 rounded-none border-r-[1px] hover:bg-slate-300"
                        >Create Purchase Invoice</Button>
                    </Link>}
                    {(stockPointType === StockPointType.RECEIVING.toString() || pharmacySize === PharmacySize.SIZE_SMALL.toString()) && <Link
                        to="/inventory/create-purchase-order"
                        preload="intent"
                    >
                        <Button
                            className={`bg-slate-50 text-black-200 rounded-none border-r-[1px] hover:bg-slate-300 ${pharmacySize === PharmacySize.SIZE_SMALL.toString() ? 'rounded-r-xs' : ''}`}
                        >Create Purchase Order</Button>
                    </Link>}
                    {<Link
                        to="/inventory/transfer-stock"
                        preload="intent"
                    >
                        <Button
                            className={`bg-slate-50 text-black-200 rounded-l-none rounded-r-xs hover:bg-slate-300 ${pharmacySize === PharmacySize.SIZE_SMALL.toString() ? 'rounded-r-xs' : ''}`}
                        >Transfer Stock</Button>
                    </Link>}
                </div>
            </div>
            <div className="grid grid-cols-4 gap-x-4">
                <OverviewCard
                    title="Total Inventory Value"
                    value={convertToCurrencyRounded(data.inventory_overview.total_inventory_value)}
                />
                <OverviewCard 
                    title="Total Inventory"
                    value={(data.inventory_overview.total_inventory_count).toString()}
                />
                <OverviewCard 
                    title="Expiring Soon"
                    value={(data.inventory_overview.expiring_soon_count).toString()}
                    link="/inventory/available-inventory/?expiring_in_30_days=true"
                />
                <OverviewCard 
                    title="Out of Stock"
                    value={(data.inventory_overview.out_of_stock_count).toString()}
                    link="/reports/out-of-stock"
                />
            </div>
            <div>
                <Card className={`bg-[#FCFCFC] flex flex-col h-full`}>
                    <div className="m-4 flex items-center text-gray-600 justify-between">
                        <p className="text-sm ml-1">Activity</p>
                        <div className="flex items-center gap-4 pr-10">
                            {[
                                { label: "All", key: "all" },
                                ...(stockPointType === StockPointType.RECEIVING.toString()
                                    ? [
                                        { label: "Purchase Orders", key: "purchase_orders" },
                                        { label: "Purchase Invoices", key: "purchase_invoices" },
                                    ]
                                    : []),
                                { label: "Purchase Requests", key: "purchase_requests" },
                                { label: "Transfers", key: "transfers" },
                            ].map(({ label, key }) => (
                                <span
                                    key={key}
                                    className={`flex items-center gap-2 border px-2 py-1 rounded-md font-semibold text-muted-foreground text-xs cursor-pointer ${
                                        activityView === key ? "bg-gray-100" : ""
                                    }`}
                                    onClick={() => setActivityView(key)}
                                >
                                    {label}
                                </span>
                            ))}
                        </div>
                    </div>
                    <hr className="my-0" />
                    {/* <CardHeader>
                        <CardTitle></CardTitle>
                        <CardDescription></CardDescription>
                    </CardHeader> */}
                    <CardContent className="max-h-[320px] overflow-scroll py-2">
                        {
                            stockPointType === StockPointType.RECEIVING.toString() && (activityView === "purchase_orders" || activityView === "all") && data.purchase_orders?.purchaseOrders.map((order) => (
                                <ActivityComponent
                                    key={order.id}
                                    label={dayjs(order.createdAt?.toDate()).format("DD MMM, YYYY HH:mm:ss")}
                                    description={<span>Purchase Order #{<span className="font-semibold">{order.orderNumber}</span>} for {order.items.length} items</span>}
                                    type="purchase_order"
                                    id={order.id}
                                />
                            ))
                        }
                        {
                            stockPointType === StockPointType.RECEIVING.toString() && (activityView === "purchase_invoices" || activityView === "all") && data.purchase_invoices?.invoices.map((invoice) => (
                                <ActivityComponent
                                    key={invoice.id}
                                    label={dayjs(invoice.createdAt?.toDate()).format("DD MMM, YYYY HH:mm:ss")}
                                    description={<span>Purchase Invoice #{<span className="font-semibold">{invoice.grnNumber}</span>} for {invoice.totalItems} items | {convertToCurrencyRounded(invoice.totalAmount)}</span>}
                                    type="purchase_invoice"
                                    id={invoice.id}
                                />
                            ))
                        }
                        {
                            (activityView === "purchase_requests" || activityView === "all") && data.purchase_requests?.purchaseRequests.map((request) => (
                                <ActivityComponent
                                    key={request.id}
                                    label={dayjs(request.createdAt?.toDate()).format("DD MMM, YYYY HH:mm:ss")}
                                    description={<span>Purchase Request #{<span className="font-semibold">{request.requestNumber}</span>} for {request.items.length} items | {Object.keys(RequestStatus).find(key => (RequestStatus as Record<string, unknown>)[key] === request.status)}</span>}
                                    type="purchase_request"
                                    id={request.id}
                                />
                            ))
                        }
                        {
                            (activityView === "transfers" || activityView === "all") && data.inventory_transfers?.transferHistory.map((transfer) => (
                                <ActivityComponent
                                    key={transfer.transferRequestId}
                                    description={`${transfer.status === TransferHistoryStatus.PENDING_APPROVAL ? 'Transfer Requested for ' : transfer.status === TransferHistoryStatus.APPROVED ? 'Transfer Approved for ' : transfer.status === TransferHistoryStatus.COMPLETED ? 'Transferred ' : 'Transfer of '} ${transfer.items.length} items from ${transfer.sourceStockPointName} to ${transfer.destinationStockPointName}`}
                                    label={dayjs(transfer.createdAt?.toDate()).format("DD MMM, YYYY HH:mm:ss")}
                                    type="transfer"
                                    id={transfer.transferRequestId}
                                />
                            ))
                        }
                    </CardContent>
                    <CardFooter className="flex-col items-start gap-2 text-sm border-t pt-2">
                        <div className="flex gap-2 font-medium leading-none">
                        Last Updated: Now
                        </div>
                    </CardFooter>
                </Card>
            </div>
            <Analytics 
                topSuppliers={data.top_suppliers} 
                expiryTimeline={data.expiry_timeline}
                stockStatusSplit={data.stock_status_split}
                nonMedicalCategoryDistribution={data.non_medical_category_distribution}
                inventoryTrend={data.inventory_trend}
            />
            <div className="grid grid-cols-4 gap-x-4">
                <Health 
                    healthScores={data.health_scores}
                />
            </div>
            <QuickLinks />
        </div>
    )
}

export default Component